import { useEffect, useState } from 'react';
import { findSimilarStudents, createStudent, getDirectoryFilters, assetUrl } from '../../services/api';
import { getRumaalColor } from '../../utils/colors';
import { invalidParentEmails } from '../../utils/parentEmails';
import { RUMAAL_RANKS, STUDENT_STATUSES } from '../../utils/constants';

const FIELD = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm transition-shadow duration-150 outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';

// Adding a student, search first.
//
// The client's reasoning, in his own words: creating a student "shouldn't be
// easy to just make a new student and done… at least that will convince them to
// search for student and then add it in". So the name is asked for on its own,
// matches appear as it is typed, and the rest of the form only opens once the
// admin has looked at them.
//
// Choosing a match is not a dead end — it adds this class to the child who is
// already there, which is exactly how a kid in two classes should be recorded.

const MAX_DIMENSION = 800;

function resizeToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('That file is not an image'));
      image.onload = () => {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const CONFIDENCE_COPY = {
  high: 'Almost certainly the same child',
  likely: 'Could well be the same child',
  possible: 'Possibly the same child',
};

export default function AddStudent({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [linkTo, setLinkTo] = useState(null);

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [status, setStatus] = useState('Active');
  const [rank, setRank] = useState('White');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [email, setEmail] = useState('');
  const [photo, setPhoto] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getDirectoryFilters()
      .then(({ data }) => setClasses(data.classes || []))
      .catch(() => setClasses([]));
  }, []);

  // Look for existing students as the name is typed, not on submit — a warning
  // after the fact is one nobody reads.
  useEffect(() => {
    if (name.trim().length < 2) {
      setMatches([]);
      return undefined;
    }
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await findSimilarStudents(name.trim());
        setMatches(data.matches || []);
      } catch {
        setMatches([]);
      } finally {
        setChecking(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [name]);

  const badEmails = invalidParentEmails(email);
  // The form opens once the admin has either seen no matches or said none apply.
  const formOpen = name.trim().length >= 2 && (matches.length === 0 || reviewed || linkTo);

  const handlePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await resizeToDataUrl(file));
      setError('');
    } catch (photoError) {
      setError(photoError.message);
    }
  };

  const handleSave = async () => {
    if (!classId) { setError('Choose a class for them'); return; }
    if (badEmails.length) { setError(`Check this email address: ${badEmails.join(', ')}`); return; }

    setSaving(true);
    setError('');
    try {
      const { data } = await createStudent({
        name: name.trim(),
        classId,
        existingProfileId: linkTo?.id || undefined,
        status,
        rumaalRank: rank,
        dateOfBirth: dateOfBirth || null,
        gender: gender || null,
        parentEmails: email,
        photo: photo || undefined,
      });
      onCreated(data);
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Could not add this student');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div className="relative h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-md">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Add a student</h2>
          <button
            onClick={onClose}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div>
            <label className={LABEL}>Their name</label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setReviewed(false); setLinkTo(null); }}
              placeholder="Start typing to check they're not already here"
              className={FIELD}
              autoFocus
            />
            {checking && <p className="mt-1 text-xs text-gray-400">Checking…</p>}
          </div>

          {matches.length > 0 && !linkTo && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900">
                {matches.length === 1 ? 'This student may already be here' : 'These students may already be here'}
              </p>
              <p className="mb-3 text-xs text-amber-800">
                Adding them again would split their attendance, results and certificates across two records.
              </p>

              <div className="space-y-2">
                {matches.map((match) => (
                  <div key={match.id} className="flex items-center gap-2 rounded-md border border-amber-200 bg-white p-2">
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
                      style={{ boxShadow: `0 0 0 2px ${getRumaalColor(match.rumaalRank)}` }}
                    >
                      {match.photoUrl
                        ? <img src={assetUrl(match.photoUrl)} alt="" className="h-full w-full object-cover" />
                        : <span className="text-[10px] font-semibold text-gray-400">
                            {match.displayName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                          </span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">{match.displayName}</div>
                      <div className="truncate text-xs text-gray-500">
                        {match.classes?.length
                          ? match.classes.map((c) => `${c.label} · ${c.instructor}`).join(' | ')
                          : 'Not in any class'}
                        {' — '}{CONFIDENCE_COPY[match.confidence]}
                      </div>
                    </div>
                    <button
                      onClick={() => setLinkTo(match)}
                      className="shrink-0 rounded-md bg-purple-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700"
                    >
                      That's them
                    </button>
                  </div>
                ))}
              </div>

              {!reviewed && (
                <button
                  onClick={() => setReviewed(true)}
                  className="mt-3 w-full rounded-md border border-dashed border-amber-400 px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100"
                >
                  None of these — this is a new student
                </button>
              )}
            </div>
          )}

          {linkTo && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3">
              <p className="text-sm text-purple-900">
                Adding this class to <strong>{linkTo.displayName}</strong>, who is already in the system.
              </p>
              <button
                onClick={() => setLinkTo(null)}
                className="shrink-0 text-xs text-purple-700 underline"
              >
                Undo
              </button>
            </div>
          )}

          {formOpen && (
            <>
              <div>
                <label className={LABEL}>Class</label>
                <select value={classId} onChange={(e) => setClassId(e.target.value)} className={FIELD}>
                  <option value="">Choose a class…</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>{cls.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} className={FIELD}>
                    {STUDENT_STATUSES.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Rank</label>
                  <select
                    value={rank}
                    onChange={(e) => setRank(e.target.value)}
                    className={FIELD}
                    disabled={status === 'Trial'}
                  >
                    {RUMAAL_RANKS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  {status === 'Trial' && (
                    <p className="mt-1 text-xs text-gray-500">Trials get a rank when they convert.</p>
                  )}
                </div>
              </div>

              {!linkTo && (
                <>
                  <div>
                    <label className={LABEL}>Date of birth</label>
                    <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} className={FIELD} />
                  </div>

                  <div>
                    <label className={LABEL}>Gender</label>
                    <div className="flex gap-2">
                      {['Girl', 'Boy', 'Other'].map((option) => (
                        <button
                          key={option}
                          onClick={() => setGender(gender === option ? '' : option)}
                          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                            gender === option
                              ? 'border-purple-600 bg-purple-600 text-white'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={LABEL}>Parent email</label>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="parent@example.com"
                      className={`${FIELD} ${badEmails.length ? 'border-red-300 bg-red-50/60' : ''}`}
                    />
                    {badEmails.length > 0 && (
                      <p className="mt-1 text-xs text-red-600">Check this address: {badEmails.join(', ')}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      You can send their invitation from the student's page once they're added.
                    </p>
                  </div>

                  <div>
                    <label className={LABEL}>Photo</label>
                    <div className="flex items-center gap-3">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
                        {photo
                          ? <img src={photo} alt="" className="h-full w-full object-cover" />
                          : <span className="text-xl text-gray-300">+</span>}
                      </div>
                      <input type="file" accept="image/*" onChange={handlePhoto} className="text-sm text-gray-600" />
                    </div>
                  </div>
                </>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-2 border-t border-gray-200 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Adding…' : linkTo ? `Add ${linkTo.displayName} to this class` : 'Add to the stable'}
                </button>
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
